import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { Dispatch, ForwardedRef, forwardRef, SetStateAction, useImperativeHandle, useRef, useState } from 'react';

interface Props {
  disabled?: boolean;
  rating: number;
  classNames?: { root?: string; starFill?: string; star?: string; starContainer?: string };
}

const Rating = forwardRef(
  (
    props: Props,
    ref: ForwardedRef<{
      value: number;
      setValue: Dispatch<SetStateAction<number>>;
    }>,
  ) => {
    const { disabled = false, rating, classNames = {} } = props;
    const [internalValue, setInternalValue] = useState(transformRatingToStarValue(rating));
    const prevPropsRef = useRef({ rating });

    if (prevPropsRef.current.rating !== rating) {
      prevPropsRef.current.rating = rating;
      setInternalValue(transformRatingToStarValue(rating));
    }

    useImperativeHandle(ref, () => ({
      value: internalValue,
      setValue: setInternalValue,
    }));

    return (
      <div className={cn(clsx('flex items-center gap-x-[3px]'), classNames.root)}>
        {Array.from({ length: 5 }, (_, i) => {
          const hasLevelValue = internalValue > i;
          const levelValue = hasLevelValue ? (internalValue - i > 1 ? 1 : internalValue - i) : 0;

          return (
            <div
              className={cn('w-[18px] flex-none', classNames.starContainer)}
              key={i}
            >
              <div className='relative pb-[100%]'>
                <div className='absolute left-0 top-0 h-full w-full'>
                  <div
                    className={cn(
                      clsx(
                        ' h-full  bg-[url("/images/product-page/rating-star.svg")] bg-[length:auto_100%]  bg-center bg-no-repeat ',
                        disabled ? 'cursor-default' : 'cursor-pointer',
                      ),
                      classNames.star,
                    )}
                    onClick={() => {
                      if (disabled) return;

                      setInternalValue?.(i + 1);
                    }}
                  >
                    <div
                      className={cn(
                        clsx(
                          ' bg-[url("/images/product-page/rating-star-fill.svg")] bg-[length:auto_100%] bg-no-repeat ',
                          'h-full',
                        ),
                        classNames.starFill,
                      )}
                      style={{ width: `${(levelValue / 1) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  },
);

const transformRatingToStarValue = (rating: number) => {
  if (!rating) return 0;

  const divided = rating / 20;
  const dividedFloor = Math.floor(divided);
  return divided - dividedFloor > 0 ? dividedFloor + 0.5 : divided;
};

export default Rating;
